/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Table column filter component
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import get from 'lodash/get';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EAutoReportIdTableAccountField } from '@pages/PageAutoReportId/components/Table/AutoReportIdTableCell';
import { MapExpensesAnalyzerTableColor } from '@pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableMapTypes';
import { EPagePaymentsTableField } from '@pages/PagePayments/const/PagePaymentsTable';
import { ESettingsAutoReportsField } from '@pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/settingsAutoReportsTableTypes';
import { ESettingsStatisticsField } from '@pages/PageSettings/components/SettingsStatistics/components/SettingsStatisticsTable/settingsStatisticsTableTypes';
import type { IRootReducer } from '@reducers/rootReducer';

import { setTableSortAction } from '../../actions/tableActions';
import { setOrder } from '../../helpers/filterAndSort';
import type { ITableReducer } from '../../reducers/tableReducer';
import { makeSelectorTableSort } from '../../selectors/tableSelectors';
import { FilterStubStyled } from '../../styled/TableStyled';
import { ETableColumnUtilHideFilters } from '../../TableColumnUtil';
import { ColumnFieldType, ColumnTranslationType, EColumnCell } from '../../types/columnTypes';
import { TableNameType } from '../../types/tableTypes';
import type { ISetTableSortPayload } from '../../types/tableTypes';
import type { IColumn } from '../TableColumns/Columns';
import Filter, { SortEnum } from './Filter';
import InnerFilter from './InnerFilter';

interface IColumnFilterProps {
    isDisableFilter?: boolean;
    isDisableSort?: boolean;
    hideFilterType?: ETableColumnUtilHideFilters;
}

interface IConnectedProps {
    sort: ITableReducer[TableNameType]['sort'];
}

interface IDispatchProps {
    setTableOrder: (payload: ISetTableSortPayload) => void;
}

// Field are not sortable and doesn't have sort icon
const unsortableFields: string[] = [
    EColumnCell.actionConflicts,
    EColumnCell.cost,
    EColumnCell.labels,
    EPagePaymentsTableField.paymentSource,
    ESettingsAutoReportsField.REMOVE,
    ESettingsStatisticsField.IP_ADDRESS,
    EAutoReportIdTableAccountField.delete,
];

const ColumnFilter =
    <Field extends ColumnFieldType, Translation extends ColumnTranslationType>
        (props: IColumn<Field, Translation> & IConnectedProps & IDispatchProps & IColumnFilterProps): JSX.Element => {
        return (
            <Filter
                headerPointColor={MapExpensesAnalyzerTableColor[props.name]}
                isSortable={!unsortableFields.includes(props.name) && !props.isDisableSort}
                name={get(props.translation, `table.${props.name}.header`, props.label)}
                sort={(props.sort.property === props.name) ?
                    props.sort.direction : SortEnum.default
                }
                tooltipMsg={get(props.translation, `table.${props.name}.description`)}
                onSort={() => props.setTableOrder({
                    property: props.name, order: setOrder(props.sort, props.name),
                })}
            >
                {
                    !props.hideFilterType ?
                        <InnerFilter {...props} />
                        :
                        props.hideFilterType === ETableColumnUtilHideFilters.STUB &&
                        <FilterStubStyled />
                }
            </Filter>
        );
    };

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    sort: makeSelectorTableSort(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setTableOrder: (payload: ISetTableSortPayload) => dispatch(setTableSortAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ColumnFilter);
